import React from 'react';
import { ICreateSourceRequest, IUpdateSourceRequest } from '../../../../models';
import { Input, Select, SelectOption, Checkbox } from '../ui';
import ScheduleField from './ScheduleField';

export type SourceFormData = ICreateSourceRequest | IUpdateSourceRequest;

interface SourceFormFieldsProps {
  formData: SourceFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => void;
  isEdit?: boolean;
}

const SourceFormFields: React.FC<SourceFormFieldsProps> = ({
  formData,
  onChange,
  isEdit = false,
}) => {
  const handleScheduleChange = (value: string) => {
    const syntheticEvent = {
      target: {
        name: 'scrapingSchedule',
        value: value,
      },
    } as React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>;

    onChange(syntheticEvent);
  };

  const handleSourceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const syntheticEvent = {
      target: {
        name: 'sourceType',
        value: e.target.value,
      },
    } as React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>;

    onChange(syntheticEvent);
  };

  const handleSearchConfigChange = (
    field: string,
    value: string | number | boolean | undefined
  ) => {
    const currentConfig = formData.searchResultsConfig || {};
    const updatedConfig = { ...currentConfig, [field]: value };

    const syntheticEvent = {
      target: {
        name: 'searchResultsConfig',
        value: updatedConfig,
      },
    } as React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>;

    onChange(syntheticEvent);
  };

  const sourceType = formData.sourceType || 'URL_LIST';

  const sourceTypeOptions: SelectOption[] = [
    { value: 'URL_LIST', label: 'Manual URL List' },
    { value: 'SITEMAP', label: 'Sitemap' },
    { value: 'SEARCH_RESULTS', label: 'Search Results' },
  ];

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* First Row: Agency ID, Name, Domain */}
      <div className="col-span-12 sm:col-span-4">
        <Input
          name="agencyId"
          label="Agency ID"
          value={formData.agencyId || ''}
          onChange={onChange}
          fullWidth
          required
          helperText="Unique identifier for the agency"
        />
      </div>
      <div className="col-span-12 sm:col-span-4">
        <Input
          name="name"
          label="Source Name"
          value={formData.name}
          onChange={onChange}
          fullWidth
          required
        />
      </div>
      <div className="col-span-12 sm:col-span-4">
        <Input
          name="domain"
          label="Domain"
          value={formData.domain || ''}
          onChange={onChange}
          fullWidth
          required
          placeholder="example.com"
          helperText="Website domain (required)"
        />
      </div>

      {/* Source Type Selection */}
      <div className="col-span-12 sm:col-span-6">
        <Select
          value={sourceType}
          label="Source Type"
          options={sourceTypeOptions}
          onChange={handleSourceTypeChange}
          fullWidth
          required
        />
      </div>

      {/* Source URL - conditional based on source type */}
      {(sourceType === 'SITEMAP' || sourceType === 'SEARCH_RESULTS') && (
        <div className="col-span-12 sm:col-span-6">
          <Input
            name="sourceUrl"
            label={sourceType === 'SITEMAP' ? 'Sitemap URL' : 'Search Results URL'}
            value={formData.sourceUrl || ''}
            onChange={onChange}
            fullWidth
            required
            helperText={
              sourceType === 'SITEMAP'
                ? 'URL to the sitemap.xml file'
                : 'URL to the search results page'
            }
          />
        </div>
      )}

      {/* Search Results Configuration */}
      {sourceType === 'SEARCH_RESULTS' && (
        <>
          <div className="col-span-12">
            <h6 className="text-lg font-medium text-gray-900 mb-2">
              Search Results Configuration
            </h6>
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Input
              label="Property Link Selector"
              value={formData.searchResultsConfig?.propertyLinkSelector || ''}
              onChange={(e) => handleSearchConfigChange('propertyLinkSelector', (e.target as HTMLInputElement).value)}
              fullWidth
              required
              placeholder="a[href*='/property/']"
              helperText="CSS selector to find property links on search results page"
            />
          </div>
          <div className="col-span-12 sm:col-span-3">
            <Input
              label="Next Page Selector"
              value={formData.searchResultsConfig?.nextPageSelector || ''}
              onChange={(e) => handleSearchConfigChange('nextPageSelector', (e.target as HTMLInputElement).value)}
              fullWidth
              placeholder="a.next-page"
              helperText="CSS selector for next page link (optional)"
            />
          </div>
          <div className="col-span-12 sm:col-span-3">
            <Input
              label="Max Pages"
              type="number"
              value={
                formData.searchResultsConfig?.maxPages === undefined
                  ? ''
                  : formData.searchResultsConfig?.maxPages
              }
              onChange={(e) => {
                const raw = (e.target as HTMLInputElement).value;
                const parsed = raw === '' ? undefined : parseInt(raw, 10) || 1;
                handleSearchConfigChange('maxPages', parsed);
              }}
              fullWidth
              placeholder="10"
              helperText="Maximum pages to scrape (optional)"
            />
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Checkbox
              checked={Boolean(formData.searchResultsConfig?.useBrowser)}
              onChange={(e) =>
                handleSearchConfigChange('useBrowser', (e.target as HTMLInputElement).checked)
              }
              label="Use headless browser"
            />
            <p className="mt-1 text-xs text-gray-500">
              Enable when the site requires JavaScript execution (uses Puppeteer)
            </p>
          </div>
        </>
      )}

      {/* URL Filter Pattern */}
      {(sourceType === 'SITEMAP' || sourceType === 'SEARCH_RESULTS') && (
        <div className="col-span-12">
          <Input
            name="urlFilterPattern"
            label="URL Filter Pattern (regex)"
            value={formData.urlFilterPattern || ''}
            onChange={onChange}
            fullWidth
            placeholder="e.g., .*property/[0-9]+.*"
            helperText="Only URLs matching this pattern will be processed (optional)"
          />
        </div>
      )}

      <div className="col-span-12">
        <Input
          name="keepQueryParams"
          label="Keep Query Parameters"
          value={formData.keepQueryParams || ''}
          onChange={onChange}
          fullWidth
          placeholder="e.g., propertyId,listingId"
          helperText="Comma-separated list of query params to retain during URL normalization"
        />
      </div>

      {/* Schedule Field - Full Width */}
      <div className="col-span-12">
        <ScheduleField
          value={formData.scrapingSchedule}
          onChange={handleScheduleChange}
          label="Processing Schedule"
          helperText="Configure when this source should be automatically processed. Leave empty for manual processing only."
        />
      </div>

      {/* Description */}
      <div className="col-span-12">
        <Input
          name="description"
          label="Description"
          value={formData.description}
          onChange={onChange}
          fullWidth
          multiline
          rows={isEdit ? 3 : 2}
        />
      </div>
    </div>
  );
};

export default SourceFormFields;
